import { NotificationType, TNotification } from '@/context/types/Notification';
import useHomeContext from '@/hooks/useHomeContext';
import { useEffect, useRef, useState } from 'react';
import { notifications } from '../data/notifications';
import { getSecondsInMilliseconds } from '@/utils/getMilliseconds';
import { replaceVariables } from '../utils/replaceVariables';

export const useNotification = () => {
  const timerRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const { notification, setNotification, setError, error } = useHomeContext()!;
  const [openToast, setOpenToast] = useState<TNotification>();
  const [acknowledgedNotif, setAcknowledgedNotif] = useState<string>();

  useEffect(() => {
    if (acknowledgedNotif && acknowledgedNotif === openToast?.type) {
      setAcknowledgedNotif(undefined);
      return;
    }

    if (notification) {
      const defaultValues = notifications.find((notif) => notif.type === notification.type);

      if (defaultValues) {
        setOpenToast({
          variant: defaultValues.variant,
          type: defaultValues.type,
          title: defaultValues.title
            ? replaceVariables(defaultValues.title ?? '', notification.data ?? {})
            : undefined,
          description: replaceVariables(defaultValues.description, notification.data ?? {})
        });

        // Clear any existing timers when new notifications come in during this time
        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }

        timerRef.current = setTimeout(() => {
          setOpenToast((prev) => {
            setAcknowledgedNotif(prev?.type);
            return undefined;
          });
          setNotification(undefined);
          setError(undefined);
        }, getSecondsInMilliseconds(10));
      }
    }
  }, [notification]);

  useEffect(() => {
    if (error) {
      const unknownErrorDefaults = notifications.find(
        (notif) => notif.type === NotificationType.UNKNOWN_ERROR
      );
      setOpenToast({
        type: NotificationType.UNKNOWN_ERROR,
        title: unknownErrorDefaults?.title,
        description: error ?? unknownErrorDefaults?.description ?? '',
        variant: unknownErrorDefaults?.variant
      });

      // Clear any existing timers when new notifications come in during this time
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      timerRef.current = setTimeout(() => {
        setOpenToast((prev) => {
          setAcknowledgedNotif(prev?.type);
          return undefined;
        });
        setNotification(undefined);
        setError(undefined);
      }, getSecondsInMilliseconds(5));
    }
  }, [error]);

  const handleCloseToast = () => {
    setOpenToast(undefined);
    setError(undefined);
    clearTimeout(timerRef.current);
  };

  return { openToast, handleCloseToast };
};
